import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { scrollIntoView } from '@theme/scrolling';
import { clamp, prefersReducedMotion } from '@theme/utilities';

const DRAG_THRESHOLD = 8;
const SWIPE_THRESHOLD = 0.16;
const FLING_VELOCITY = 0.35;
const TRANSITION_DURATION = 320;
const TRANSITION_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

/**
 * @param {number} value
 * @param {number} length
 */
function wrapIndex(value, length) {
  return ((value % length) + length) % length;
}

/**
 * PDP-only infinite carousel.
 *
 * This component intentionally does not use native horizontal scrolling. The
 * browser can therefore keep vertical page scrolling while one interruptible
 * transform owns every horizontal gesture and transition. Leading/trailing
 * sentinels make the loop visual; they are replaced with their identical real
 * slide immediately after a transition, with no physical scroll edge.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} track
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLElement} [thumbnailsContainer]
 * @property {HTMLElement} [slideshowControls]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class PdpCarousel extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  requiredRefs = ['scroller', 'track', 'slideshowContainer'];

  #controller;
  #gestureController;
  #activeGesture;
  #animationController;
  #resizeObserver;
  #sentinels = [];
  #trackSignature = '';
  #current = 0;
  #position = 0;
  #activeTrackIndex = 1;
  #dragging = false;
  #animationTimer;
  #resizeFrame;
  #settleResolvers = [];
  #suppressClickUntil = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { scroller } = this.refs;
    const slides = this.slides;

    this.setAttribute('in-viewport', '');
    scroller.scrollLeft = 0;
    scroller.addEventListener('dragstart', this.#preventNativeDrag, { signal, capture: true });
    // The theme's declarative zoom listener runs on document during capture.
    // Listen one level earlier so a drag-generated click cannot reach it.
    window.addEventListener('click', this.#handleClickCapture, { signal, capture: true });

    if (!slides.length) return;

    this.#setCurrent(clamp(this.initialSlideIndex, 0, slides.length - 1), false, 'select');

    if (slides.length === 1) {
      this.setAttribute('ready', '');
      this.#updateControlsVisibility();
      return;
    }

    this.#createSentinels();

    scroller.addEventListener('pointerdown', this.#handlePointerDown, { signal, capture: true });
    scroller.addEventListener('touchstart', this.#handleTouchStart, { signal, passive: true, capture: true });
    this.addEventListener('pointerenter', this.#handlePointerEnter, { signal });

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#dragging) return;

      cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = requestAnimationFrame(() => {
        this.#stopAnimationAtRenderedPosition();
        this.#jumpToTrack(this.current + 1);
      });
    });
    this.#resizeObserver.observe(this.refs.slideshowContainer);

    requestAnimationFrame(() => {
      this.#jumpToTrack(this.current + 1);
      this.#updateControlsVisibility();
      requestAnimationFrame(() => this.setAttribute('ready', ''));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#gestureController?.abort();
    this.#activeGesture = undefined;
    this.#animationController?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#resizeFrame);
    clearTimeout(this.#animationTimer);
    this.#resolveSettled();

    const { track } = this.refs;
    track.style.removeProperty('transform');
    track.style.removeProperty('transition');

    for (const sentinel of this.#sentinels) sentinel.remove();
    this.#sentinels = [];
    this.#trackSignature = '';
    this.#dragging = false;
    this.removeAttribute('dragging');
    this.removeAttribute('transitioning');
    this.removeAttribute('ready');
    this.removeAttribute('in-viewport');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'initial-slide' || oldValue === newValue || !this.isConnected || !this.hasAttribute('ready')) return;

    this.select(parseInt(newValue, 10) || 0, undefined, { animate: false });
  }

  get slides() {
    return /** @type {HTMLElement[]} */ (this.refs.slides ?? []).filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );
  }

  get current() {
    return this.#current;
  }

  get initialSlideIndex() {
    return parseInt(this.getAttribute('initial-slide') ?? '0', 10) || 0;
  }

  get infinite() {
    return this.hasAttribute('infinite');
  }

  get disabled() {
    return this.getAttribute('disabled') === 'true';
  }

  get previousIndex() {
    return this.current - 1;
  }

  get nextIndex() {
    return this.current + 1;
  }

  /**
   * @param {number|string|{id: string}} input
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  async select(input, event, options = {}) {
    let slides = this.slides;
    if (this.disabled || !slides.length) return;

    let requestedIndex;
    let revealedSlide = false;

    if (typeof input === 'number') {
      requestedIndex = input;
    } else if (typeof input === 'string') {
      requestedIndex = parseInt(input, 10);
    } else if (input && 'id' in input) {
      const requestedSlide = /** @type {HTMLElement | undefined} */ (
        (this.refs.slides ?? []).find((slide) => slide.getAttribute('slide-id') === input.id)
      );
      if (!requestedSlide) return;

      if (requestedSlide.hasAttribute('hidden')) {
        requestedSlide.setAttribute('reveal', '');
        requestedSlide.setAttribute('aria-hidden', 'false');
        revealedSlide = true;
      }

      slides = this.slides;
      requestedIndex = slides.indexOf(requestedSlide);
    }

    if (requestedIndex === undefined || Number.isNaN(requestedIndex)) return;

    event?.preventDefault();

    if (revealedSlide || this.#signatureFor(slides) !== this.#trackSignature) {
      this.#rebuildSentinels();
      this.#jumpToTrack(clamp(this.current + 1, 1, slides.length));
    }

    const previousIndex = this.current;
    const index = this.infinite
      ? wrapIndex(requestedIndex, slides.length)
      : clamp(requestedIndex, 0, slides.length - 1);
    const instant = prefersReducedMotion() || options.animate === false;

    let trackIndex = index + 1;
    if (this.infinite && !instant && previousIndex === slides.length - 1 && requestedIndex >= slides.length) {
      trackIndex = slides.length + 1;
    } else if (this.infinite && !instant && previousIndex === 0 && requestedIndex < 0) {
      trackIndex = 0;
    }

    this.#setCurrent(index, event != null, 'select');
    await this.#animateToTrack(trackIndex, instant);
  }

  /**
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  next(event, options) {
    return this.select(this.nextIndex, event, options);
  }

  /**
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  previous(event, options) {
    return this.select(this.previousIndex, event, options);
  }

  #createSentinels() {
    const slides = this.slides;
    const { track } = this.refs;
    if (slides.length <= 1) return;

    const before = this.#cloneSentinel(slides[slides.length - 1], 'before');
    const after = this.#cloneSentinel(slides[0], 'after');

    track.prepend(before);
    track.append(after);
    this.#sentinels = [before, after];
    this.#trackSignature = this.#signatureFor(slides);
  }

  #rebuildSentinels() {
    this.#stopAnimationAtRenderedPosition();
    for (const sentinel of this.#sentinels) sentinel.remove();
    this.#sentinels = [];
    this.#createSentinels();
  }

  /** @param {HTMLElement[]} slides */
  #signatureFor(slides) {
    return slides.map((slide, index) => slide.getAttribute('slide-id') ?? `${index}`).join('|');
  }

  /**
   * @param {HTMLElement} source
   * @param {'before'|'after'} position
   */
  #cloneSentinel(source, position) {
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');
    clone.removeAttribute('hidden');
    clone.removeAttribute('reveal');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.setAttribute('data-pdp-carousel-sentinel', position);
    clone.style.setProperty('--slideshow-timeline', 'none');

    clone.querySelectorAll('deferred-media, product-model').forEach((media) => {
      const poster = media.querySelector('img');
      if (poster) media.replaceWith(poster.cloneNode(true));
      else media.remove();
    });
    clone.querySelectorAll('video, iframe, model-viewer, template, script').forEach((node) => node.remove());

    for (const element of [clone, ...clone.querySelectorAll('*')]) {
      element.removeAttribute('id');
      element.removeAttribute('ref');
      element.removeAttribute('data-media-id');
      element.removeAttribute('data-view-transition-type');

      if (element instanceof HTMLImageElement || element instanceof HTMLAnchorElement) element.draggable = false;

      for (const attribute of [...element.attributes]) {
        if (attribute.name.startsWith('on:')) element.removeAttribute(attribute.name);
      }
    }

    return clone;
  }

  get #trackSlides() {
    return /** @type {HTMLElement[]} */ ([...this.refs.track.children]).filter(
      (child) => child instanceof HTMLElement && child.matches('slideshow-slide') && !child.hasAttribute('hidden')
    );
  }

  /** @param {number} trackIndex */
  #targetPosition(trackIndex) {
    const slide = this.#trackSlides[trackIndex];
    const { scroller } = this.refs;
    if (!slide) return this.#position;

    return slide.offsetLeft - (scroller.clientWidth - slide.offsetWidth) / 2;
  }

  /** @param {number} position */
  #applyPosition(position) {
    this.refs.track.style.transform = `translate3d(${-position}px, 0, 0)`;
  }

  #readRenderedPosition() {
    const transform = getComputedStyle(this.refs.track).transform;
    if (!transform || transform === 'none') return this.#position;

    try {
      return -new DOMMatrixReadOnly(transform).m41;
    } catch {
      return this.#position;
    }
  }

  /** @param {number} trackIndex */
  #jumpToTrack(trackIndex) {
    this.#animationController?.abort();
    this.#animationController = undefined;
    clearTimeout(this.#animationTimer);

    const { track } = this.refs;
    track.style.transition = 'none';
    this.#position = this.#targetPosition(trackIndex);
    this.#activeTrackIndex = trackIndex;
    this.#applyPosition(this.#position);
    this.removeAttribute('transitioning');
  }

  /**
   * @param {number} trackIndex
   * @param {boolean} instant
   */
  #animateToTrack(trackIndex, instant) {
    this.#stopAnimationAtRenderedPosition();

    const { track } = this.refs;
    const targetPosition = this.#targetPosition(trackIndex);
    this.#activeTrackIndex = trackIndex;
    if (instant || Math.abs(this.#position - targetPosition) < 0.5) {
      this.#position = targetPosition;
      track.style.transition = 'none';
      this.#applyPosition(this.#position);
      this.#finishAtTrack(trackIndex);
      return Promise.resolve();
    }

    const controller = new AbortController();
    this.#animationController = controller;
    const { signal } = controller;

    track.style.transition = 'none';
    this.#applyPosition(this.#position);
    void track.offsetWidth;
    track.style.transition = `transform ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`;
    this.setAttribute('transitioning', '');

    const finish = () => {
      if (this.#animationController !== controller) return;
      this.#finishAtTrack(trackIndex);
    };

    track.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === track && event.propertyName === 'transform') finish();
      },
      { signal }
    );
    this.#animationTimer = setTimeout(finish, TRANSITION_DURATION + 80);

    this.#position = targetPosition;
    this.#applyPosition(this.#position);

    return new Promise((resolve) => this.#settleResolvers.push(resolve));
  }

  /** @param {number} trackIndex */
  #finishAtTrack(trackIndex) {
    this.#animationController?.abort();
    this.#animationController = undefined;
    clearTimeout(this.#animationTimer);

    const slideCount = this.slides.length;
    let settledTrackIndex = trackIndex;
    if (trackIndex === 0) settledTrackIndex = slideCount;
    else if (trackIndex === slideCount + 1) settledTrackIndex = 1;

    const { track } = this.refs;
    track.style.transition = 'none';
    this.#position = this.#targetPosition(settledTrackIndex);
    this.#activeTrackIndex = settledTrackIndex;
    this.#applyPosition(this.#position);
    this.removeAttribute('transitioning');
    this.#resolveSettled();
  }

  #stopAnimationAtRenderedPosition() {
    if (!this.hasAttribute('transitioning')) return;

    const renderedPosition = this.#readRenderedPosition();
    this.#animationController?.abort();
    this.#animationController = undefined;
    clearTimeout(this.#animationTimer);

    const { track } = this.refs;
    track.style.transition = 'none';
    this.#position = renderedPosition;
    this.#applyPosition(this.#position);
    void track.offsetWidth;
    this.removeAttribute('transitioning');
    this.#resolveSettled();
  }

  /** @param {number} [position] */
  #nearestTrackIndex(position = this.#position) {
    const trackSlides = this.#trackSlides;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    trackSlides.forEach((_, index) => {
      const distance = Math.abs(this.#targetPosition(index) - position);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  #normalizeBoundaryForGesture() {
    const slideCount = this.slides.length;
    const nearestTrackIndex = this.#nearestTrackIndex();
    let trackIndex = this.#activeTrackIndex;

    const previousTrackIndex = clamp(trackIndex - 1, 0, slideCount + 1);
    const nextTrackIndex = clamp(trackIndex + 1, 0, slideCount + 1);
    const adjacentSpan = Math.max(
      Math.abs(this.#targetPosition(trackIndex) - this.#targetPosition(previousTrackIndex)),
      Math.abs(this.#targetPosition(nextTrackIndex) - this.#targetPosition(trackIndex)),
      1
    );

    // A dot or thumbnail can request a non-adjacent slide. If that long
    // transition is interrupted early, use the visually nearest slide instead
    // of treating a far-away intended target as the gesture's starting point.
    if (Math.abs(this.#position - this.#targetPosition(trackIndex)) > adjacentSpan * 1.05) {
      trackIndex = nearestTrackIndex;
    }

    let realTrackIndex;

    if (trackIndex === 0) realTrackIndex = slideCount;
    else if (trackIndex === slideCount + 1) realTrackIndex = 1;
    else {
      this.#activeTrackIndex = trackIndex;
      return trackIndex;
    }

    const offset = this.#position - this.#targetPosition(trackIndex);
    this.#position = this.#targetPosition(realTrackIndex) + offset;
    this.#activeTrackIndex = realTrackIndex;
    this.#applyPosition(this.#position);
    return realTrackIndex;
  }

  /** @param {number} trackIndex */
  #logicalIndex(trackIndex) {
    const slideCount = this.slides.length;
    if (trackIndex === 0) return slideCount - 1;
    if (trackIndex === slideCount + 1) return 0;
    return clamp(trackIndex - 1, 0, slideCount - 1);
  }

  /**
   * @param {number} index
   * @param {boolean} userInitiated
   * @param {'select'|'scroll'} trigger
   */
  #setCurrent(index, userInitiated, trigger) {
    const slides = this.slides;
    if (!slides.length) return;

    const normalized = clamp(index, 0, slides.length - 1);
    const previousIndex = this.#current;
    this.#current = normalized;

    if (this.refs.current) this.refs.current.textContent = `${normalized + 1}`;

    slides.forEach((slide, slideIndex) => slide.setAttribute('aria-hidden', `${slideIndex !== normalized}`));
    for (const controls of [this.refs.thumbnails, this.refs.dots]) {
      controls?.forEach((control, controlIndex) => control.setAttribute('aria-selected', `${controlIndex === normalized}`));
    }

    if (this.refs.previous) this.refs.previous.disabled = false;
    if (this.refs.next) this.refs.next.disabled = false;

    this.#centerSelectedThumbnail(normalized, trigger === 'select' ? 'smooth' : 'instant');

    if (normalized === previousIndex) return;

    const slide = slides[normalized];
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: normalized,
        previousIndex,
        userInitiated,
        trigger,
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #centerSelectedThumbnail(index, behavior) {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    const { thumbnailsContainer, slideshowControls } = this.refs;
    if (!selectedThumbnail || !thumbnailsContainer || !slideshowControls) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;
    if (this.refs.slideshowControls instanceof HTMLElement) {
      this.refs.slideshowControls.hidden = this.slides.length <= 1;
    }
  }

  #resolveSettled() {
    for (const resolve of this.#settleResolvers.splice(0)) resolve();
  }

  #preventNativeDrag = (event) => event.preventDefault();

  #handleClickCapture = (event) => {
    if (performance.now() > this.#suppressClickUntil) return;
    if (!event.composedPath().includes(this)) return;

    this.#suppressClickUntil = 0;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #handlePointerEnter = () => this.setAttribute('actioned', '');

  /**
   * Creates one input-agnostic horizontal gesture. Mouse/pointer and iOS touch
   * listeners feed coordinates into the same state machine.
   * @param {number} startX
   * @param {number} startY
   */
  #createGesture(startX, startY) {
    this.#activeGesture?.finish(true);
    this.#gestureController?.abort();
    this.#stopAnimationAtRenderedPosition();

    let startTrackIndex = this.#normalizeBoundaryForGesture();
    startTrackIndex = clamp(startTrackIndex, 1, this.slides.length);
    this.#setCurrent(this.#logicalIndex(startTrackIndex), true, 'scroll');

    const { scroller } = this.refs;
    const startPosition = this.#position;
    const minimumPosition = this.#targetPosition(startTrackIndex - 1);
    const maximumPosition = this.#targetPosition(startTrackIndex + 1);
    let previousX = startX;
    let previousTime = performance.now();
    let velocity = 0;
    let moved = false;
    let finished = false;

    /** @param {boolean} cancelled */
    const finish = (cancelled) => {
      if (finished) return;
      finished = true;

      if (!moved) {
        this.#setCurrent(this.#logicalIndex(startTrackIndex), true, 'scroll');
        this.#animateToTrack(startTrackIndex, false);
        return;
      }

      this.#suppressClickUntil = performance.now() + 500;
      this.#dragging = false;
      this.removeAttribute('dragging');

      const distance = this.#position - startPosition;
      const slideWidth = this.#trackSlides[startTrackIndex]?.offsetWidth || scroller.clientWidth;
      const shouldAdvance =
        !cancelled && (Math.abs(distance) >= slideWidth * SWIPE_THRESHOLD || Math.abs(velocity) >= FLING_VELOCITY);
      const direction = shouldAdvance ? Math.sign(distance || velocity) : 0;
      const targetTrackIndex = clamp(startTrackIndex + direction, startTrackIndex - 1, startTrackIndex + 1);

      this.#setCurrent(this.#logicalIndex(targetTrackIndex), true, 'scroll');
      this.#animateToTrack(targetTrackIndex, false);
    };

    /**
     * @param {number} clientX
     * @param {number} clientY
     * @param {Event} moveEvent
     */
    const move = (clientX, clientY, moveEvent) => {
      if (finished) return false;

      const travelledX = startX - clientX;
      const travelledY = startY - clientY;

      if (!moved) {
        if (Math.max(Math.abs(travelledX), Math.abs(travelledY)) < DRAG_THRESHOLD) return true;

        if (Math.abs(travelledY) > Math.abs(travelledX)) {
          finish(true);
          return false;
        }

        moved = true;
        this.#dragging = true;
        this.#suppressClickUntil = performance.now() + 500;
        this.setAttribute('dragging', '');
      }

      if (moveEvent.cancelable) moveEvent.preventDefault();
      moveEvent.stopImmediatePropagation();

      const now = performance.now();
      const elapsed = Math.max(now - previousTime, 1);
      const instantaneousVelocity = (previousX - clientX) / elapsed;
      velocity = velocity * 0.55 + instantaneousVelocity * 0.45;
      previousX = clientX;
      previousTime = now;

      this.#position = clamp(startPosition + travelledX, minimumPosition, maximumPosition);
      this.#applyPosition(this.#position);
      return true;
    };

    const gesture = { finish, move };
    this.#activeGesture = gesture;
    return gesture;
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    // Touch Events are used deliberately on phones. They are more consistent
    // than Pointer Events in mobile Safari for a transform-driven drag surface.
    if (event.pointerType === 'touch') return;
    if (!event.isPrimary || event.button !== 0) return;
    if (this.disabled || this.slides.length <= 1 || this.#dragging) return;
    if (!(event.target instanceof Element) || event.target.closest('model-viewer, video, iframe')) return;

    event.preventDefault();

    const { scroller } = this.refs;
    const gesture = this.#createGesture(event.clientX, event.clientY);
    const controller = new AbortController();
    this.#gestureController = controller;
    const { signal } = controller;
    let captured = false;

    const finish = (cancelled) => {
      controller.abort();
      if (this.#gestureController === controller) this.#gestureController = undefined;
      if (this.#activeGesture === gesture) this.#activeGesture = undefined;

      if (captured && scroller.hasPointerCapture(event.pointerId)) scroller.releasePointerCapture(event.pointerId);
      gesture.finish(cancelled);
    };

    const onPointerMove = (moveEvent) => {
      const active = gesture.move(moveEvent.clientX, moveEvent.clientY, moveEvent);
      if (!active) {
        finish(true);
        return;
      }

      if (this.#dragging && !captured) {
        try {
          scroller.setPointerCapture(event.pointerId);
          captured = true;
        } catch {
          // Document-level listeners still keep the drag active.
        }
      }
    };

    const onPointerUp = () => finish(false);
    const onPointerCancel = () => finish(true);

    document.addEventListener('pointermove', onPointerMove, { signal, passive: false });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerCancel, { signal });
    scroller.addEventListener('lostpointercapture', onPointerCancel, { signal });
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    if (event.touches.length !== 1 || this.disabled || this.slides.length <= 1 || this.#dragging) return;
    if (!(event.target instanceof Element) || event.target.closest('model-viewer, video, iframe')) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const { scroller } = this.refs;
    const identifier = touch.identifier;
    const gesture = this.#createGesture(touch.clientX, touch.clientY);
    const controller = new AbortController();
    this.#gestureController = controller;
    const { signal } = controller;

    const finish = (cancelled) => {
      controller.abort();
      if (this.#gestureController === controller) this.#gestureController = undefined;
      if (this.#activeGesture === gesture) this.#activeGesture = undefined;
      gesture.finish(cancelled);
    };

    const onTouchMove = (moveEvent) => {
      const activeTouch = [...moveEvent.touches].find((item) => item.identifier === identifier);
      if (!activeTouch) return;

      if (!gesture.move(activeTouch.clientX, activeTouch.clientY, moveEvent)) finish(true);
    };

    const onTouchEnd = (endEvent) => {
      if (![...endEvent.changedTouches].some((item) => item.identifier === identifier)) return;
      finish(false);
    };

    const onTouchCancel = (cancelEvent) => {
      if (![...cancelEvent.changedTouches].some((item) => item.identifier === identifier)) return;
      finish(true);
    };

    scroller.addEventListener('touchmove', onTouchMove, { signal, passive: false });
    scroller.addEventListener('touchend', onTouchEnd, { signal, passive: true });
    scroller.addEventListener('touchcancel', onTouchCancel, { signal, passive: true });
  };
}

if (!customElements.get('pdp-carousel-component')) {
  customElements.define('pdp-carousel-component', PdpCarousel);
}
